<template>
  <CModal
    :show.sync="visible"
    :title="modalTitle"
    @hidden="onHidden"
    centered
    size="lg"
    add-content-classes="fit-content-modal"
    :close-on-backdrop="false"
  >

    <template #header>
        <div class="d-flex align-items-start">
          <CIcon name="cipCopy" size="custom-size" width="36" height="36"
                 class="mr-3 icon-circle icon-circle--primary"/>
          <div>
            <h5 class="modal-title mb-0">
              <translate translate-context="collaborators.virtualusers">Credentials</translate>
            </h5>
            <p class="mb-0">
              <translate translate-context="collaborators.virtualusers">
                Share these credentials with your collaborators: they will access the platform and will be able to upload photos directly to the event gallery.
              </translate>
            </p>
          </div>
        </div>
        <div>
          <CButtonClose @click="close"/>
        </div>
    </template>

    <!-- Single Credential Display -->
    <div v-if="!isMultiple && credentials" class="credentials-display p-3">
      <CFormGroup>
        <CLabel>
          <translate translate-context="collaborators.virtualusers">Email</translate>
        </CLabel>
        <CInputGroup>
          <CInput :value="credentials.email" readonly/>
          <CInputGroupAppend>
            <CButton color="secondary" @click="copyField(credentials.email)">
              <CIcon name="cilCopy"/>
            </CButton>
          </CInputGroupAppend>
        </CInputGroup>
      </CFormGroup>

      <CFormGroup>
        <CLabel>
          <translate translate-context="collaborators.virtualusers">Password</translate>
        </CLabel>
        <CInputGroup>
          <CInput :value="credentials.password" :type="showPassword ? 'text' : 'password'" readonly/>
          <CInputGroupAppend>
            <CButton color="secondary" @click="showPassword = !showPassword">
              <CIcon :name="showPassword ? 'cilEyeSlash' : 'cilEye'"/>
            </CButton>
            <CButton color="secondary" @click="copyField(credentials.password)">
              <CIcon name="cilCopy"/>
            </CButton>
          </CInputGroupAppend>
        </CInputGroup>
      </CFormGroup>

      <CFormGroup>
        <CLabel>
          <translate translate-context="collaborators.virtualusers">Domain</translate>
        </CLabel>
        <CInput :value="credentials.domain" readonly/>
      </CFormGroup>
    </div>

    <!-- Multiple Credentials Display (Bulk Reset) -->
    <div v-if="isMultiple && credentialsArray.length > 0" class="credentials-table">
      <CDataTable
        :items="credentialsArray"
        :fields="tableFields"
      >
        <template #password="{item}">
          <td style="border-bottom: 1px solid #ddd;">
            <div class="d-inline-block font-weight-medium"
                 style="background: #F7F4FF;padding: 4px 6px;border-radius: 6px">{{ item.password }}
            </div>
          </td>
        </template>
        <template #link="{item}">
          <td style="color: #A9A8C2">
            {{ dashboardUrl }}
          </td>
        </template>
        <template #email="{item}">
          <td>
            <span>{{ item.email }}</span>
          </td>
        </template>
        <template #credentials="{item}">
          <td class="text-center">
            <CButton color="light" size="sm" class="btn-copy" @click="copyRowCredentials(item)">
              <CIcon name="cipCopy" class="mr-2"/>
               <translate translate-context="collaborators.virtualusers">Copy</translate>
            </CButton>
          </td>
        </template>
      </CDataTable>
    </div>

    <!-- info Banner -->
    <PAlert color="primary" class="mb-4" icon="cipInfo">
      <div>
        <div class="font-weight-semi-bold">
          <translate translate-context="collaborators.virtualusers">
            Save credentials
          </translate>
        </div>
        <div class="mt-1">
          <translate translate-context="collaborators.virtualusers">
            Make sure you copy them and save them safely, they will not be shown again
          </translate>
        </div>
      </div>
    </PAlert>
    <!-- Copy All Section -->
    <div class="mt-4 p-3" style="background-color: #FAFAFA;border-radius: 12px ">
      <div class="d-flex justify-content-between align-items-end">
        <div class="mr-4">
          <div class="font-weight-medium mb-2">
            <translate translate-context="collaborators.virtualusers">
              Copy credentials
            </translate>
          </div>
          <div style="color: #8887A0;line-height: 1.5;">
            <translate translate-context="collaborators.virtualusers">
              Formatted text ready to be shared via email, WhatsApp or secure messaging
            </translate>
          </div>
        </div>
        <CButton color="light" size="sm" @click="copyAll" class="btn-copy">
          <CIcon name="cipCopy" class="mr-2"/>
          <translate translate-context="collaborators.virtualusers">Copy all</translate>
        </CButton>
      </div>

    </div>

    <template #footer>
      <CButton color="primary" @click="close">
        <translate translate-context="collaborators.virtualusers">Close</translate>
      </CButton>
    </template>
  </CModal>
</template>

<script>
import {mapGetters} from 'vuex'
import PAlert from "@/components/PAlert.vue";

export default {
  name: 'VirtualUserCredentialsModal',
  components: {PAlert},

  data() {
    return {
      visible: false,
      credentials: null,
      showPassword: false,
      tableFields: [
        {
          key: 'email',
          label: this.$pgettext('collaborators.virtualusers', 'Email')
        },
        {
          key: 'password',
          label: this.$pgettext('collaborators.virtualusers', 'Password')
        },
        {
          key: 'link',
          label: this.$pgettext('collaborators.virtualusers', 'Access Link'),
        },
        {
          key: 'credentials',
          label: this.$pgettext('collaborators.virtualusers', 'Credentials'),
          _style: 'width: 120px; text-align: center;'
        }
      ]
    }
  },

  computed: {
    ...mapGetters('event', [
      'currentEventName'
    ]),

    dashboardUrl() {
      return 'https://dashboard.getpica.com'
    },

    isMultiple() {
      return Array.isArray(this.credentials)
    },

    credentialsArray() {
      return this.isMultiple ? this.credentials : []
    },

    modalTitle() {
      return this.$pgettext('collaborators.virtualusers', 'Virtual User Credentials')
    }
  },

  methods: {
    show(credentials) {
      this.credentials = credentials
      this.visible = true
      this.showPassword = false
    },

    close() {
      this.visible = false
    },

    onHidden() {
      // Clear credentials from memory when modal closes
      this.credentials = null
      this.showPassword = false

      // Clear from Vuex store
      this.$store.dispatch('collaborators/clearVirtualUserCredentials')
    },

    copyField(text) {
      this.copyToClipboard(text)
      this.notifySuccess(
        this.$pgettext('collaborators.virtualusers', 'Copied to clipboard')
      )
    },

    copyRowCredentials(item) {
      const text = this.formatCredential(item)
      this.copyToClipboard(text)
      this.notifySuccess(
        this.$pgettext('collaborators.virtualusers', 'Credentials copied to clipboard')
      )
    },

    copyAll() {
      const text = this.isMultiple
        ? this.formatMultipleCredentials()
        : this.formatSingleCredential()

      this.copyToClipboard(text)
      this.notifySuccess(
        this.$pgettext('collaborators.virtualusers', 'All credentials copied to clipboard')
      )
    },

    formatCredential(cred) {
      const serviceName = this.currentEventName || 'Virtual User'
      return `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${serviceName}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🔗 ${this.dashboardUrl}
Email: ${cred.email}
Password: ${cred.password}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
⚠️  Keep this password secure
`.trim()
    },

    formatSingleCredential() {
      if (!this.credentials) return ''
      return this.formatCredential(this.credentials)
    },

    formatMultipleCredentials() {
      if (!this.credentialsArray.length) return ''

      const serviceName = this.currentEventName || 'Virtual Users'
      let text = `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${serviceName}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🔗 ${this.dashboardUrl}

`
      this.credentialsArray.forEach((cred) => {
        text += `Email: ${cred.email}
Password: ${cred.password}

`
      })

      text += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
⚠️  Keep these passwords secure
`
      return text.trim()
    },

    copyToClipboard(text) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text)
      } else {
        // Fallback for older browsers
        const textarea = document.createElement('textarea')
        textarea.value = text
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
      }
    }
  }
}
</script>

<style>
.credentials-display {
  background-color: #FAFAFA;
  border-radius: 12px
}


.credentials-table td {
  border-bottom: 1px solid #E1DFFB !important;
}

.credentials-table tr:last-child td {
  border-bottom: none !important;
}


.credentials-table th {
  text-transform: uppercase !important;
  font-size: 12px;
}
</style>
